import { differenceInSeconds } from "date-fns";
import {
  DEFAULT_COOKIE_EXPIRY_DAYS,
  COOKIE_PATH,
  COOKIE_EXPIRY_DATE,
} from "./constant";

export const convertHexToRGB = (hex) => {
  // check if it's a rgba
  if (hex.match("rgba")) {
    let triplet = hex.slice(5).split(",").slice(0, -1).join(",");
    return triplet;
  }

  let c;
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    c = hex.substring(1).split("");
    if (c.length === 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]];
    }
    c = "0x" + c.join("");

    return [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(",");
  }
};

// Cookie utility functions
export const setCookie = (name, value, days = DEFAULT_COOKIE_EXPIRY_DAYS) => {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = "expires=" + date.toUTCString();
  document.cookie = name + "=" + value + ";" + expires + ";path=" + COOKIE_PATH;
};

export const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

export const deleteCookie = (name) => {
  document.cookie =
    name + "=; expires=" + COOKIE_EXPIRY_DATE + "; path=" + COOKIE_PATH + ";";
};

function currentYPosition(elm) {
  if (!window && !elm) return;

  if (elm) return elm.scrollTop;
  // Firefox, Chrome, Opera, Safari
  if (window.pageYOffset) return window.pageYOffset;
  // Internet Explorer 6 - standards mode
  if (document.documentElement && document.documentElement.scrollTop)
    return document.documentElement.scrollTop;
  // Internet Explorer 6, 7 and 8
  if (document.body.scrollTop) return document.body.scrollTop;
  return 0;
}

function elmYPosition(elm) {
  var y = elm.offsetTop;
  var node = elm;
  while (node.offsetParent && node.offsetParent !== document.body) {
    node = node.offsetParent;
    y += node.offsetTop;
  }
  return y;
}

export function scrollTo(scrollableElement, elmID) {
  var elm = document.getElementById(elmID);

  if (!elmID || !elm) {
    return;
  }

  var startY = currentYPosition(scrollableElement);
  var stopY = elmYPosition(elm);

  var distance = stopY > startY ? stopY - startY : startY - stopY;
  if (distance < 100) {
    scrollTo(0, stopY);
    return;
  }
  var speed = Math.round(distance / 50);
  if (speed >= 20) speed = 20;
  var step = Math.round(distance / 25);
  var leapY = stopY > startY ? startY + step : startY - step;
  var timer = 0;
  if (stopY > startY) {
    for (var i = startY; i < stopY; i += step) {
      setTimeout(
        (function (leapY) {
          return () => {
            scrollableElement.scrollTo(0, leapY);
          };
        })(leapY),
        timer * speed,
      );
      leapY += step;
      if (leapY > stopY) leapY = stopY;
      timer++;
    }
    return;
  }
  for (let i = startY; i > stopY; i -= step) {
    setTimeout(
      (function (leapY) {
        return () => {
          scrollableElement.scrollTo(0, leapY);
        };
      })(leapY),
      timer * speed,
    );
    leapY -= step;
    if (leapY < stopY) leapY = stopY;
    timer++;
  }
  return false;
}

export function getTimeDifference(date) {
  let difference = differenceInSeconds(new Date(), date);

  if (difference < 60) return `${Math.floor(difference)} sec`;
  else if (difference < 3600) return `${Math.floor(difference / 60)} min`;
  else if (difference < 86400) return `${Math.floor(difference / 3660)} h`;
  else if (difference < 86400 * 30)
    return `${Math.floor(difference / 86400)} d`;
  else if (difference < 86400 * 30 * 12)
    return `${Math.floor(difference / 86400 / 30)} mon`;
  else return `${(difference / 86400 / 30 / 12).toFixed(1)} y`;
}

export const utcTimeToIST = (time) => {
  const [h, m] = time.split(":").map(Number);

  let totalMinutes = h * 60 + m + 330; // UTC → IST
  totalMinutes %= 1440;

  let hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const period = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;

  return `${hours}.${String(minutes).padStart(2, "0")} ${period}`;
};

export const ist12HourToUTC = (time) => {
  const match = time.match(/(\d{1,2})[.:](\d{2})\s?(AM|PM)/i);
  if (!match) return "00:00:00";

  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const meridian = match[3].toUpperCase();

  // convert to 24-hour IST
  if (meridian === "PM" && hours !== 12) hours += 12;
  if (meridian === "AM" && hours === 12) hours = 0;

  // convert IST → UTC
  let utcMinutes = hours * 60 + minutes - 330;

  if (utcMinutes < 0) utcMinutes += 1440;

  const utcHours = Math.floor(utcMinutes / 60);
  const utcMins = utcMinutes % 60;

  return `${String(utcHours).padStart(2, "0")}:${String(utcMins).padStart(
    2,
    "0",
  )}:00`;
};

export const createUTCDateTimeFromIst = (date, time) => {
  if (!date || !time) return "";

  // Normalize time (supports: "8 PM", "8.00 PM", "8.30 PM")
  const match = time.trim().match(/^(\d{1,2})(?:[.:](\d{2}))?\s*(AM|PM)$/i);

  if (!match) {
    return "";
  }

  let hours = Number(match[1]);
  let minutes = Number(match[2] || 0);
  const modifier = match[3].toUpperCase();

  if (modifier === "PM" && hours !== 12) hours += 12;
  if (modifier === "AM" && hours === 12) hours = 0;

  const istDate = new Date(
    `${date}T${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00+05:30`
  );

  if (isNaN(istDate.getTime())) {
    return "";
  }

  return istDate.toISOString();
}

export const sanitizeHTML = (html) => {
  if (!html) return "";

  // Replace \n, \r, or /n with <br /> to preserve line breaks
  const withLineBreaks = html
    .replace(/\r\n|\n|\r/g, "<br />")
    .replace(/\s?\/n\s?/g, "<br />");

  const doc = new DOMParser().parseFromString(withLineBreaks, "text/html");
  const allowedTags = [
    "B", "I", "U", "STRONG", "EM", "P", "BR", "UL", "OL", "LI",
    "SPAN", "DIV", "A", "H1", "H2", "H3", "H4", "H5", "H6"
  ];

  const sanitize = (node) => {
    const children = Array.from(node.childNodes);
    for (const child of children) {
      if (child.nodeType === 1) { // Node.ELEMENT_NODE
        if (!allowedTags.includes(child.tagName)) {
          // Unwrap: move children of disallowed tag to parent node
          while (child.firstChild) {
            node.insertBefore(child.firstChild, child);
          }
          node.removeChild(child);
          // Since we moved children, we need to sanitize the new children of 'node'
          // Actually, we can just call sanitize(node) again or just continue
          // But it's safer to re-run sanitization on the children that were moved
          sanitize(node);
          return; // Exit current loop as the children collection is now stale
        } else {
          // Remove all attributes except safe ones
          const allowedAttrs = ["href", "target", "rel", "style"];
          const attrs = Array.from(child.attributes);
          for (const attr of attrs) {
            const attrName = attr.name.toLowerCase();

            // Allow 'style' attribute for all tags to preserve inline CSS
            const isAllowedAttr = allowedAttrs.includes(attrName);

            if (!isAllowedAttr) {
              child.removeAttribute(attr.name);
            }

            // Basic check for javascript: in href
            if (
              attrName === "href" &&
              attr.value.toLowerCase().startsWith("javascript:")
            ) {
              child.removeAttribute(attr.name);
            }
          }
          sanitize(child);
        }
      }
    }
  };

  sanitize(doc.body);
  return doc.body.innerHTML;
};


